import { useSearchParams } from 'react-router-dom';
import { useTasks, useProjects } from '@/hooks/useTasks.ts';
import { TaskRow } from '@/components/TaskRow.tsx';
import { TaskDrawer } from '@/components/TaskDrawer.tsx';
import { PageHeader, SectionLabel } from '@/components/ui.tsx';
import { addDays, startOfDay } from '@/lib/dates.ts';
import { OPEN_STATUSES } from '@/lib/views.ts';
import type { TaskFilter, TaskWithRelations } from '@shared/types';

const STALE_DAYS = 14;

/**
 * The weekly review.
 *
 * Four questions in the order worth asking them: what did I miss, what has
 * gone cold, what never got scheduled, and what did I actually finish. The
 * last is not decoration — a review that only lists failures is one you stop
 * opening.
 */
export function Review() {
  const [searchParams, setSearchParams] = useSearchParams();
  const openTaskId = searchParams.get('task');

  const now = new Date();
  const weekAgo = addDays(startOfDay(now), -7).toISOString();
  const staleCutoff = addDays(startOfDay(now), -STALE_DAYS).toISOString();

  const slipped: TaskFilter = { status: OPEN_STATUSES, due_before: now.toISOString(), top_level_only: true };
  const stale: TaskFilter = { status: OPEN_STATUSES, updated_before: staleCutoff, top_level_only: true };
  const unscheduled: TaskFilter = { status: OPEN_STATUSES, no_due_date: true, top_level_only: true };
  const finished: TaskFilter = { completed_after: weekAgo, top_level_only: true };

  const slippedQ = useTasks(slipped, [{ field: 'due_at', dir: 'asc' }]);
  const staleQ = useTasks(stale, [{ field: 'updated_at', dir: 'asc' }]);
  const unscheduledQ = useTasks(unscheduled, [{ field: 'priority', dir: 'desc' }]);
  const finishedQ = useTasks(finished, [{ field: 'completed_at', dir: 'desc' }]);

  const { data: projects } = useProjects();
  const projectNames = new Map((projects ?? []).map((p) => [p.id, p.name]));

  // A task that is both overdue and stale belongs in the first section only —
  // the same row appearing twice makes the review feel longer than it is.
  const slippedIds = new Set((slippedQ.data ?? []).map((t) => t.id));
  const staleOnly = (staleQ.data ?? []).filter((t) => !slippedIds.has(t.id));

  function openTask(id: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('task', id);
      return next;
    });
  }

  function closeTask() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('task');
      return next;
    });
  }

  const render = (tasks: TaskWithRelations[]) =>
    tasks.map((task) => (
      <TaskRow
        key={task.id}
        task={task}
        onOpen={openTask}
        projectName={task.project_id ? (projectNames.get(task.project_id) ?? null) : null}
      />
    ));

  const needsAttention = (slippedQ.data?.length ?? 0) + staleOnly.length + (unscheduledQ.data?.length ?? 0);
  const finishedCount = finishedQ.data?.length ?? 0;
  const loaded = slippedQ.isSuccess && staleQ.isSuccess && unscheduledQ.isSuccess;

  return (
    <div className="mx-auto w-full max-w-[1120px] px-5 py-8 sm:px-8">
      <PageHeader
        title="Weekly review"
        subtitle={now.toLocaleDateString(undefined, {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        })}
      />

      {/* A clean week used to render as four consecutive empty sentences, which
          looked identical to a broken page. It now gets a deliberate state. */}
      {loaded && needsAttention === 0 && (
        <div className="mb-10 rounded-md border border-line bg-raised px-5 py-6">
          <span aria-hidden="true" className="mb-3 block h-px w-10 bg-brass" />
          <p className="t-title mb-1 text-2xl text-ink">Nothing needs rescuing.</p>
          <p className="t-meta max-w-prose">
            Nothing overdue, nothing gone cold, and everything open has a date.
            {finishedCount > 0 && ` You closed ${finishedCount} this week.`}
          </p>
        </div>
      )}

      <Section
        label="Slipped"
        hint="Past due and still open"
        count={slippedQ.data?.length}
        empty="Nothing overdue."
      >
        {render(slippedQ.data ?? [])}
      </Section>

      <Section
        label="Gone cold"
        hint={`Open, untouched for ${STALE_DAYS} days`}
        count={staleOnly.length}
        empty="Everything open has been touched recently."
      >
        {render(staleOnly)}
      </Section>

      <Section
        label="Unscheduled"
        hint="Open with no date"
        count={unscheduledQ.data?.length}
        empty="Everything open has a date."
      >
        {render(unscheduledQ.data ?? [])}
      </Section>

      <Section
        label="Finished"
        hint="Completed in the last 7 days"
        count={finishedCount}
        empty="Nothing closed this week yet."
      >
        {render(finishedQ.data ?? [])}
      </Section>

      {openTaskId && <TaskDrawer taskId={openTaskId} onClose={closeTask} />}
    </div>
  );
}

function Section({
  label,
  hint,
  count,
  empty,
  children,
}: {
  label: string;
  hint: string;
  count: number | undefined;
  empty: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-10">
      <SectionLabel count={count}>{label}</SectionLabel>
      <p className="t-meta mb-2 text-ink-3">{hint}</p>
      {count === 0 ? (
        <p className="t-meta border-t border-line py-3 text-ink-3">{empty}</p>
      ) : (
        <ul className="border-t border-line">{children}</ul>
      )}
    </section>
  );
}
